/**
 * Facet dropdown options for the File Manager list, extracted from the page so
 * the option lists can be unit tested without mounting the table.
 *
 * The keys here are HALF of a contract with `Api::V1::FilesController`'s
 * `filterable(...)` declaration; the other half is pinned by
 * `components/table/config-parity.test.ts` via `FILES_TABLE.facetKeys`, and by
 * `xop-brain/apps/crm-api/test/controllers/concerns/declared_keys_export_test.rb`
 * on the server side.
 */
import type { FacetDef } from "@crm-src/components/table/TableFilterBar";
import type { FacetKeysOf } from "@crm-src/components/table/configs";
import { FILES_TABLE } from "@crm-src/components/table/configs";
import { personLabel, type PersonLike } from "@crm-src/components/table/person-label";
import { buildTableParams } from "@crm-src/components/table/table-query";
import type { FacetValues, SortDir } from "@crm-src/components/table/table-query";
import type { FilesListParams } from "@crm-src/lib/files-api";

/**
 * The File Manager facet keys, DERIVED from the shared config rather than
 * re-typed — so there is exactly one place a key can be misspelled, and
 * misspelling it there breaks the parity assertions.
 */
export type FileFacetKey = FacetKeysOf<typeof FILES_TABLE>;

/**
 * Compile-time proof that every declared facet key is a param
 * `FilesListParams` actually carries — i.e. one the API implements. A key that
 * only exists in the config otherwise compiles fine and fails at RUNTIME as a
 * param the server drops.
 */
type FacetKeysAreFilterFields =
  FileFacetKey extends keyof FilesListParams ? true : never;
const _facetKeysAreFilterFields: FacetKeysAreFilterFields = true;
void _facetKeysAreFilterFields;

/**
 * The KINDS the "File type" facet offers.
 *
 * This list is the client half of a closed allow-list: the server's
 * `Api::V1::FilesController::FILE_TYPE_KINDS` maps each `value` below to one
 * code-literal SQL predicate, and a value it does not know contributes no
 * clause (the list stays as wide as it was). Sending a MIME string instead was
 * the obvious alternative and is not viable: the page cannot enumerate every
 * content type in the tenant's tree to populate the dropdown, and
 * `TableFilterBar` drops an option-less facet entirely — so the control would
 * have rendered nothing at all.
 *
 * Ordered by how often an operator reaches for them, not alphabetically.
 * Folders lead because "show me only folders" is the one kind that is about the
 * tree rather than about a file's contents.
 *
 * There is no `video` or `audio` option, and that is deliberate. Uploading is
 * the only way a file's `content_type` is ever set, and both server upload
 * paths sniff the mime and reject anything outside
 * `UploadSecurity::ALLOWED_IMAGE_TYPES + ALLOWED_DOC_TYPES` — a union with no
 * `video/*` and no `audio/*` in it. Offering those kinds gave the operator two
 * filters that always came back empty. If the server ever accepts media
 * uploads, BOTH this list and `FILE_TYPE_KINDS` in `files_controller.rb` have
 * to grow the kind back.
 */
export const FILE_TYPE_KINDS = [
  { value: "folder", label: "Folders" },
  { value: "image", label: "Images" },
  { value: "pdf", label: "PDFs" },
  { value: "document", label: "Documents" },
  { value: "text", label: "Text" },
] as const;

/**
 * Build the File Manager filter-bar facets.
 *
 * The Created By options come from data the page can already ask for — the user
 * roster — so no endpoint exists just to populate a dropdown. `useUsers()`
 * answers 403 with `[]` for a non-admin; the facet is still RETURNED, because
 * dropping an option-less facet from the rendered controls is `TableFilterBar`'s
 * job and every list page inherits it. That is also the right behaviour here for
 * a second reason: a non-admin's file list is ALREADY restricted to the files
 * they created (`FilesController#index`), so a Created By facet could only ever
 * narrow it to the same rows or to none.
 *
 * Stays a pure function of its inputs.
 */
export function buildFileFacetDefs(users: PersonLike[]): FacetDef<FileFacetKey>[] {
  return [
    {
      key: "file_type",
      label: "File type",
      options: FILE_TYPE_KINDS.map((kind) => ({ value: kind.value, label: kind.label })),
    },
    {
      key: "created_by_id",
      label: "Created By",
      options: users.map((u) => ({
        value: String(u.id),
        label: personLabel(u),
      })),
    },
  ];
}

export interface FileListQuery {
  sort: string;
  dir: SortDir;
  facets: FacetValues<FileFacetKey>;
  page: number;
  perPage: number;
  search: string;
  /** Current folder. Absent = the root listing. */
  parentId?: string;
  /**
   * `useTableQuery().sortExplicit` — whether the viewer actually PICKED this
   * sort or just landed on the page default. Accepted, and deliberately not
   * forwarded; see `buildFileListParams`.
   */
  sortExplicit: boolean;
}

/**
 * The exact params the File Manager list request sends.
 *
 * Lives here rather than inline in the page for one reason: the `sortExplicit`
 * handling below is a DECISION, not a pass-through, and a decision needs a
 * test.
 *
 * `buildTableParams` OMITS `sort`/`dir` when a search is active and
 * `sortExplicit` is false, so the server can rank by search RELEVANCE instead
 * of by a column. Three crm-api list actions (invoices/quotes, contacts, tasks)
 * do exactly that, each guarding the branch with `unless sort_requested?`.
 *
 * `Api::V1::FilesController` is NOT one of them. It has no relevance branch at
 * all, and its no-sort fallback is `by_name` — `ORDER BY files.name ASC`. So on
 * THIS page dropping the pair buys nothing and costs something real: land on
 * the File Manager (modified-descending, the declared default), type into the
 * search box, and the list silently flips to name-ascending while
 * `SortableHeader` goes on rendering the descending arrow on Modified. The
 * header and the data disagree — the exact defect class this whole effort
 * exists to remove.
 *
 * Hence the hardcoded `true`. `sortExplicit` is still taken as an argument so
 * the page keeps threading the real value through and this stays the ONE place
 * that decides what to do with it: if `FilesController` ever grows a relevance
 * branch, the fix is to forward it here and delete this note, rather than to
 * rediscover the question at the call site. Pinned by "KEEPS the sort while
 * searching, because this API has no relevance branch".
 *
 * Deliberately scoped to this page. Of the five pages that pass `sortExplicit`,
 * invoices/quotes and contacts have real relevance branches and must keep
 * forwarding it, and Resellers is unaffected because its no-sort fallback
 * (`order(:name)`) is identical to its declared default.
 */
export function buildFileListParams(
  { sort, dir, facets, page, perPage, search, parentId, sortExplicit: _sortExplicit }: FileListQuery,
): FilesListParams {
  return {
    ...buildTableParams({ sort, dir }, facets, page, perPage, search, true),
    ...(parentId ? { parent_id: parentId } : {}),
  };
}
